/**
 * Conversation Tracker Hook
 *
 * Tracks session identity and local conversation state for the UI.
 * The backend owns durable persistence to Second Brain.
 */

import { useCallback, useRef } from 'react';

const DEBUG_VERBOSE = process.env.REACT_APP_DEBUG_LIVE === '1';

export interface ConversationMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  timestamp: string;
}

// Session ID key in sessionStorage
const SESSION_ID_KEY = 'aida_session_id';

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `aida_${timestamp}_${random}`;
}

/**
 * Get or create session ID from sessionStorage
 * This persists across Gemini reconnects but resets when the browser tab closes
 */
function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    console.log('[ConversationTracker] Created new session:', sessionId);
  }
  return sessionId;
}

/**
 * Hook for tracking and pushing conversation messages
 */
export function useConversationTracker() {
  const sessionIdRef = useRef<string | null>(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = getOrCreateSessionId();
    console.log('[ConversationTracker] Using session:', sessionIdRef.current);
  }
  const messagesRef = useRef<ConversationMessage[]>([]);

  // Add a message to the conversation
  const addMessage = useCallback((role: ConversationMessage['role'], content: string) => {
    if (!content.trim()) return;

    const message: ConversationMessage = {
      role,
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };

    messagesRef.current.push(message);
    if (DEBUG_VERBOSE) {
      console.log('[ConversationTracker] Added message:', role, content.slice(0, 50) + '...');
    }
  }, []);

  // Add a tool call to the conversation (logged as tool role)
  const addToolCall = useCallback((toolName: string, args: Record<string, unknown>, result: unknown) => {
    const content = `Tool: ${toolName}\nInput: ${JSON.stringify(args)}\nOutput: ${JSON.stringify(result)}`;
    addMessage('tool', content);
  }, [addMessage]);

  const flushConversation = useCallback(async () => {
    return;
  }, []);

  // Start a new session (clear messages and generate new session ID)
  const startNewSession = useCallback(() => {
    messagesRef.current = [];
    sessionIdRef.current = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionIdRef.current);
    console.log('[ConversationTracker] Started new session:', sessionIdRef.current);
    return sessionIdRef.current;
  }, []);

  return {
    sessionId: sessionIdRef.current!,
    addMessage,
    addToolCall,
    flushConversation,
    startNewSession,
    messageCount: messagesRef.current.length,
  };
}
